import { format, parseISO } from "date-fns"
import i18next from "i18next"
import { BikeFleetType, StatusEnum } from "./types/bikeFleetType"
import { isNotEmpty } from "utils"
import { Bike } from "models"
import ExcelJS, { Fill } from "exceljs"
import { saveAs } from "file-saver"
import { operators } from "utils/constants"

//Function for Transform Bike fleet
export const transformBikeFleet = (bikes: Array<Partial<Bike>>) => {
	const newArrayOfBikeFleet: Array<BikeFleetType> = bikes.map((bike) => ({
		choice: bike.choice,
		fnuciCode: bike.bike_id,
		operator: bike.operator,
		brand: bike.bike_description.brand,
		model: bike.bike_description.model,
		type: bike.bike_description.gear_type,
		color: bike.bike_description.color,
		status: bike.bike_status.toString() as `${StatusEnum}`,
		proofOfRegistration: "boolean",
		activation: new Date(bike.bike_last_registered_by.create_date) as unknown as string,
	}))
	return newArrayOfBikeFleet
}

//Function for normalize text
export const normalizeText = (text: string): string => {
	return text
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
}

// Function for transform data and normalize Text
export function includeInToObjectNormalizedValue(object, chains) {
	return Object.entries(object).some(([key, val]) => {
		const value = key === "activation" ? format(val as Date, "dd/MM/yyyy") : val
		if (!isNotEmpty(value)) return false
		const normalizedVal = normalizeText(value.toString())
		return normalizedVal.includes(chains)
	})
}

export const exportData = async (data: Array<BikeFleetType>) => {
	const workbook = new ExcelJS.Workbook()
	const worksheet = workbook.addWorksheet(i18next.t("bikeFleet.addWorksheet", { ns: "bike" }))

	const titleCellStyle: Partial<ExcelJS.Cell> = {
		font: { size: 15 },
		alignment: { horizontal: "center", vertical: "middle" },
		fill: { type: "pattern", pattern: "solid", fgColor: { argb: "fff3cc" } },
		border: {
			top: { style: "thin" },
			left: { style: "thin" },
			bottom: { style: "thin" },
			right: { style: "thin" },
		},
	}

	const titleRow = worksheet.addRow([i18next.t("bikeFleet.exportTitle", { ns: "bike" })])
	worksheet.mergeCells("A1:H1")
	Object.assign(titleRow.getCell(1), titleCellStyle)

	const headers = [
		i18next.t("bikeFleet.columnsXLS.fnuciCode", { ns: "bike" }),
		i18next.t("bikeFleet.columnsXLS.operator", { ns: "bike" }),
		i18next.t("bikeFleet.columnsXLS.brand", { ns: "bike" }),
		i18next.t("bikeFleet.columnsXLS.model", { ns: "bike" }),
		i18next.t("bikeFleet.columnsXLS.type", { ns: "bike" }),
		i18next.t("bikeFleet.columnsXLS.color", { ns: "bike" }),
		i18next.t("bikeFleet.columnsXLS.status", { ns: "bike" }),
		i18next.t("bikeFleet.columnsXLS.activation", { ns: "bike" }),
	]

	const headerRow = worksheet.addRow(headers)
	headerRow.height = 28
	headerRow.font = { bold: true, size: 15 }

	const fillStyle: ExcelJS.Fill = {
		type: "pattern",
		pattern: "solid",
		fgColor: { argb: "FFE697" },
	}
	const borderStyleFirstCell: Partial<ExcelJS.Borders> = {
		top: { style: "thin" },
		left: { style: "thin" },
		bottom: { style: "thin" },
	}
	const borderStyleLastCell: Partial<ExcelJS.Borders> = {
		top: { style: "thin" },
		right: { style: "thin" },
		bottom: { style: "thin" },
	}
	const borderStyleMiddleCells: Partial<ExcelJS.Borders> = {
		top: { style: "thin" },
		bottom: { style: "thin" },
	}
	const alignmentStyle: Partial<ExcelJS.Alignment> = { vertical: "middle", horizontal: "center" }

	headerRow.eachCell((cell, number) => {
		cell.fill = fillStyle
		cell.alignment = alignmentStyle
		if (number === 1) cell.border = borderStyleFirstCell
		else if (number === headers.length) cell.border = borderStyleLastCell
		else cell.border = borderStyleMiddleCells
	})

	if (!data || data.length === 0) {
		console.warn(i18next.t("bikeFleet.noSelectionExportBikeFleet", { ns: "bike" }))
		return
	}

	const alignmentCell: Partial<ExcelJS.Alignment> = { vertical: "middle", horizontal: "center" }

	data.forEach((bike) => {
		const row = worksheet.addRow([
			bike.fnuciCode,
			operators.find((operator) => operator.id_api === bike.operator).name,
			bike.brand,
			bike.model,
			bike.type,
			bike.color,
			getLabelFromStatusNumber(bike.status as unknown as StatusEnum),
			format(parseISO(bike.activation), "dd/MM/yyyy"),
		])
		row.eachCell((cell) => {
			cell.alignment = alignmentCell
			cell.font = { size: 14 }
		})
		row.height = 16
	})

	const fixedWidth = 30
	worksheet.columns.forEach((column) => {
		column.width = fixedWidth
	})

	const buffer = await workbook.xlsx.writeBuffer()
	const blob = new Blob([buffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	})
	saveAs(blob, i18next.t("bikeFleet.exportFileName", { ns: "bike" }))
}

export const bikeFleetStatusList = [StatusEnum.IN_SERVICE, StatusEnum.STOLEN, StatusEnum.DESTRUCT]

//Function for change Status number to Text
export const getLabelFromStatusNumber = (statusNumber: StatusEnum) => {
	switch (statusNumber) {
		case StatusEnum.SOLD:
			return i18next.t("bikeFleet.status.sold", { ns: "bike" })
		case StatusEnum.STOLEN:
			return i18next.t("bikeFleet.status.stolen", { ns: "bike" })
		case StatusEnum.IN_SERVICE:
			return i18next.t("bikeFleet.status.inService", { ns: "bike" })
		case StatusEnum.DESTRUCT:
			return i18next.t("bikeFleet.status.destroy", { ns: "bike" })
		case StatusEnum.ALL:
			return i18next.t("bikeFleet.status.all", { ns: "bike" })
		default:
			return ""
	}
}

//Function for know is electric bike
export const isVaeBike = (isElectric: boolean, gear_type: string) => {
	const gearTypeArray = gear_type.split(" ")
	const MF_SUFFIXE_ELECTRIQUE = "ELECTRIQUE"
	const isMfElectric = gearTypeArray[gearTypeArray.length - 1] === MF_SUFFIXE_ELECTRIQUE
	return isElectric ? true : isMfElectric
}
